import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTheme } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftAvatar from "components/SoftAvatar";
import DefaultUserAvatar from "layouts/pages/account/components/Header/components/DefaultUserAvatar";
import ProtectedButton from "authentication/ProtectedButton";
import { PERMISSIONS } from "constants/portal";
import { THEME_COLORS } from "constants/theme";
import UploadModal from "layouts/pages/account/components/Header/components/UploadModal";
import RemovePictureModal from "layouts/pages/account/components/Header/components/RemovePictureModal";
import StatusBadge from "cxfcomponents/StatusBadge";

function Header({ otherUser, isOwner, action, label = "User" }) {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [anchorEl, setAnchorEl] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [, setCompanyLogo] = useState("");
  const [userAvatar, setUserAvatar] = useState(otherUser?.image || "");

  useEffect(() => {
    setUserAvatar(otherUser?.image || "");
  }, [otherUser?.image]);

  const hasImage = Boolean(userAvatar?.trim());
  const name = `${otherUser?.first_name || ""} ${otherUser?.last_name || ""}`.trim();
  const closeMenu = () => setAnchorEl(null);
  const handleMenuExited = () => {
    if (pendingAction === "upload") setShowUploadModal(true);
    if (pendingAction === "remove") setShowRemoveModal(true);
    setPendingAction(null);
  };

  return (
    <SoftBox
      display="flex"
      alignItems="center"
      sx={{
        flexWrap: "wrap",
        gap: { xs: 2, sm: 3 },
        pb: { xs: 3, lg: 4 },
        mb: { xs: 3, lg: 4 },
        borderBottom: "1px solid rgba(140, 150, 170, 0.18)"
      }}
    >
      <SoftBox position="relative" sx={{ flexShrink: 0 }}>
        <SoftAvatar
          src={hasImage ? userAvatar : undefined}
          alt={name || "User"}
          variant="rounded"
          sx={{
            width: { xs: 84, sm: 112 },
            height: { xs: 84, sm: 112 },
            borderRadius: "8px",
            border: "1px solid var(--portal-surface-border)",
            boxShadow: "none"
          }}
        >
          {!hasImage && (
            <DefaultUserAvatar
              firstName={otherUser?.first_name}
              lastName={otherUser?.last_name}
              size="xxl"
              border="unset"
              variant="rounded"
              open={false}
            />
          )}
        </SoftAvatar>
        <ProtectedButton permissionName={PERMISSIONS.EDIT_USERS} isOwner={isOwner || pathname === "/home/profile"}>
          <IconButton
            aria-label="Edit profile image"
            title="Edit profile image"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setAnchorEl(event.currentTarget);
            }}
            sx={{
              position: "absolute",
              right: -8,
              bottom: -8,
              zIndex: 2,
              width: 36,
              height: 36,
              backgroundColor: isDarkMode ? THEME_COLORS.DARK.SECONDARY_BG : THEME_COLORS.LIGHT.SECONDARY_BG,
              color: isDarkMode ? "#aa8bfd" : "#7443ff",
              border: "2px solid #7443ff",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
              "&:hover": { backgroundColor: isDarkMode ? THEME_COLORS.DARK.ACTIVE_NAVBAR_BG : "#f0ecff" },
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </ProtectedButton>
      </SoftBox>

      <SoftBox sx={{ minWidth: 0, flex: { xs: "1 1 calc(100% - 110px)", sm: "1 1 auto" } }}>
        <SoftTypography variant="overline" sx={{ color: isDarkMode ? "#aa8bfd" : "#7443ff", letterSpacing: "0.08em" }}>
          {label}
        </SoftTypography>
        <SoftTypography variant="h3" fontWeight="bold" className="white-to-dark" sx={{ lineHeight: 1.15, overflowWrap: "anywhere" }}>
          {name || otherUser?.email_address || "User"}
        </SoftTypography>
        <SoftTypography variant="button" color="text" sx={{ display: "block", mt: 1, overflowWrap: "anywhere" }}>
          {otherUser?.email_address}
        </SoftTypography>
        <SoftBox mt={1} display="flex" alignItems="center" gap={1.5}>
          {otherUser?.status && <StatusBadge status={otherUser.status} />}
        </SoftBox>
      </SoftBox>

      {action && <SoftBox sx={{ ml: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" } }}>{action}</SoftBox>}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu} TransitionProps={{ onExited: handleMenuExited }}>
        <MenuItem onClick={() => { setPendingAction("upload"); closeMenu(); }}>Change picture</MenuItem>
        {hasImage && <MenuItem onClick={() => { setPendingAction("remove"); closeMenu(); }}>Remove picture</MenuItem>}
      </Menu>
      <UploadModal
        openUploadModal={showUploadModal}
        setOpenUploadModal={setShowUploadModal}
        pathname={pathname}
        setCompanyLogo={setCompanyLogo}
        setUserAvatar={setUserAvatar}
        targetUser={{ auth0_id: otherUser?.auth0_id, tenant_id: otherUser?.tenant_id }}
      />
      <RemovePictureModal
        openRemovePictureModal={showRemoveModal}
        setOpenRemovePictureModal={setShowRemoveModal}
        setCompanyLogo={setCompanyLogo}
        setUserAvatar={setUserAvatar}
        targetUser={{ auth0_id: otherUser?.auth0_id, tenant_id: otherUser?.tenant_id }}
      />
    </SoftBox>
  );
}

export default Header;
